import { TrueBase, css, html } from "../TrueBase/TrueBase";

export class TrueGrid extends TrueBase {
  static get properties() {
    return {
      gap: {
        type: Boolean,
      },
      columnsPerRow: {
        type: Number,
        reflect: true,
      },
      maxMediumScreenSize: {
        type: Number,
      },
      widths: {
        type: Array,
      },
      widthsMd: {
        type: Array,
      },
      repeatWidths: {
        type: Boolean,
      },
      equalHeight: {
        type: Boolean,
        reflect: true,
      },
      hasOverflow: {
        type: Boolean,
      },
      resizeObserver: {
        type: ResizeObserver,
        attribute: false,
      },
      gridLoaded: {
        type: Boolean,
        reflect: false,
        attribute: false,
      },
    };
  }

  constructor() {
    super();
    this.widths = [];
    this.widthsMd = [];
    this.maxMediumScreenSize = 1024;
  }
  connectedCallback() {
    super.connectedCallback && super.connectedCallback();
    this.resizeObserver = new ResizeObserver(
      this._handleSizeAvailable.bind(this)
    );
    this.resizeObserver.observe(this);
  }
  disconnectedCallback() {
    this.resizeObserver.unobserve(this);
  }
  _handleSizeAvailable() {
    if (!this.gridLoaded) {
      this.gridLoaded = true;
    } else {
      this.setWidths();
    }
  }

  firstUpdated() {
    if (typeof this.columnsPerRow !== "undefined") {
      this.style.setProperty("--columns-per-row", this.columnsPerRow);
    }
    this.setWidths();
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === "columnsPerRow") {
        this.style.setProperty("--columns-per-row", this.columnsPerRow);
      }

      if (propName === "widths") {
        this.setWidths();
      }
    });
  }

  setWidths() {
    if (this.widths.length > 0) {
      let w = this.widths;
      if (this.widthsMd.length > 0) {
        if (window.visualViewport.width < this.maxMediumScreenSize) {
          w = this.widthsMd;
        } else {
          w = this.widths;
        }
      }

      let slots = this.shadowRoot.querySelectorAll("slot");
      let nodes = slots[0]
        .assignedNodes()
        .filter((n) => n.nodeName !== "#text");
      if (nodes.length > 0) {
        nodes.forEach((n, i) => {
          n.style.setProperty(
            "grid-column",
            `span ${this.repeatWidths ? w[i % w.length] : w[i]}`
          );
        });
      }
    }
  }

  render() {
    return html` <slot></slot> `;
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          display: grid;
          grid-template-columns: repeat(
            auto-fit,
            minmax(
              var(--grid-column-min-width, 20rem),
              var(--grid-column-max-width, 1fr)
            )
          );
          margin-top: var(--grid-margin-top, 2rem);
          padding: var(--grid-padding, 0);
          justify-content: var(--grid-justify-content, center);
          align-items: var(--grid-align-items, center);
        }

        :host([hasOverflow]) {
          display: flex;
          overflow-y: hidden;
          overflow-x: auto;
          scrollbar-width: var(--grid-overflow-item-moz-width, thin);
          scrollbar-color: var(--grid-overflow-scrollbar-color, white)
            var(--grid-overflow-scrollbar-thumb-color, #a0a0a5);
        }

        :host(:hover) {
          scrollbar-color: var(--grid-overflow-scrollbar-thumb-color, #a0a0a5)
            var(--grid-overflow-scrollbar-color, white);
        }

        :host([hasOverflow]) ::slotted(*) {
          flex: 1 0 auto;
          width: var(--grid-overflow-item-width, 350px);
        }

        :host([hasOverflow])::-webkit-scrollbar {
          background-color: var(--grid-overflow-scrollbar-color, white);
          width: 16px;
        }
        /* background of the scrollbar except button or resizer */
        :host([hasOverflow])::-webkit-scrollbar-track {
          background-color: var(--grid-overflow-scrollbar-track-color, white);
        }

        :host([hasOverflow])::-webkit-scrollbar-thumb {
          border-radius: 16px;
          border: var(--grid-overflow-scrollbar-border, 5px solid white);
          transition: background-color 0.3s ease;
        }

        :host([hasOverflow]:hover)::-webkit-scrollbar-thumb {
          background-color: var(--grid-overflow-scrollbar-thumb-color, #a0a0a5);
        }

        :host([hasOverflow])::-webkit-scrollbar-thumb:hover {
          background-color: var(--grid-overflow-scrollbar-thumb-color, #a0a0a5);
          border: var(--grid-overflow-scrollbar-thumb-hover, 4px solid #eeeeee);
        }

        /* set button(top and bottom of the scrollbar) */
        :host([hasOverflow])::-webkit-scrollbar-button {
          display: none;
        }

        :host([columnsPerRow]) {
          grid-template-columns: repeat(
            var(--columns-per-row, 3),
            var(--columns-per-row-sizing, 1fr)
          );
        }

        :host([justifyContent="center"]) {
          justify-content: center;
        }

        :host([gap]) {
          grid-gap: var(--grid-gap, 16px);
        }

        :host([equalHeight="false"]) {
          align-items: flex-start;
        }

        @media not all and (min-resolution: 0.001dpcm) {
          @supports (-webkit-appearance: none) {
            :host([hasOverflow]) ::slotted(*) {
              margin: var(--grid-gap, 16px);
            }
          }
        }

        @media screen and (-ms-high-contrast: active),
          screen and (-ms-high-contrast: none) {
          /* IE10+ specific styles go here */
          :host {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: flex-start;
            align-items: flex-start;
          }

          ::slotted(*) {
            flex: 1 1 auto;
            min-width: var(--grid-column-min-width, 250px);
            margin: var(--flex-grid-gap, 16px);
            width: calc(
              (100% / var(--flex-columns-per-row, 3)) -
                (var(--flex-grid-gap, 16px) * 2)
            );
          }
        }

        @media screen and (max-width: 35rem) {
          :host {
            grid-template-columns: repeat(1, 1fr);
          }

          :host([columnsPerRow]) {
            grid-template-columns: repeat(1, 1fr);
          }

          ::slotted(*) {
            grid-column: span 1 !important;
          }
        }
      `,
    ];
  }
}

window.customElements.get("true-grid") ||
  window.customElements.define("true-grid", TrueGrid);
